/**
 * Ajutoarele adresei de livrare: formatare si dedublarea candidatilor.
 *
 * Formularul propriu-zis traieste in `AddressSheet` — un singur loc, deschis de
 * oriunde e nevoie de el (pagina de intrare, meniu, cos, finalizare). Doua
 * ferestre scrise separat ar fi divergat la prima corectura de geocodare, iar
 * geocodarea e chiar partea care se corecteaza.
 */

import type { DropoffAddressDto } from "@deliveryapp/shared";

import type { GeocodeCandidate } from "../api/client";

/** Furnizorii de geocodare pot intoarce acelasi punct prin doua surse. Doua
 * butoane identice il fac pe client sa creada ca trebuie sa ghiceasca intre
 * ele, asa ca afisam o singura varianta pentru acelasi text si aceeasi cladire. */
export function dedupeCandidates(candidates: GeocodeCandidate[]): GeocodeCandidate[] {
  const seen = new Set<string>();
  return candidates.filter((candidate) => {
    // Clientul vede doar adresa formatata, nu coordonatele. Daca doua surse o
    // afiseaza identic dar dau puncte usor diferite, nu exista informatie dupa
    // care omul sa le poata deosebi; prima (cea mai bine clasata) castiga.
    const key = candidate.formatted.trim().toLocaleLowerCase("ro-RO");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** „Strada Alba Iulia 4, bl. A2, ap. 12, Timișoara" */
export function formatAddress(address: DropoffAddressDto): string {
  const parts = [
    [address.street, address.streetNumber].filter(Boolean).join(" "),
    address.building === null || address.building === undefined ? null : `bl. ${address.building}`,
    address.apartment === null || address.apartment === undefined ? null : `ap. ${address.apartment}`,
    address.city,
  ];
  return parts.filter((part) => typeof part === "string" && part.length > 0).join(", ");
}

/** „Alba Iulia 4" — pentru pastila din antet, unde nu incape decat esentialul. */
export function formatAddressShort(address: DropoffAddressDto): string {
  const street = address.street.replace(/^(strada|str\.?|bulevardul|bd\.?|b-dul|calea|aleea|piata|piața|pța\.?)\s+/i, "");
  return [street, address.streetNumber].filter((part) => typeof part === "string" && part.length > 0).join(" ");
}

/** Detaliile pentru curier, intr-un rand: „bl. A2 · et. 3 · ap. 12 · interfon 12". */
export function formatAddressDetails(address: DropoffAddressDto): string {
  const parts = [
    address.building ? `bl. ${address.building}` : null,
    address.floor ? `et. ${address.floor}` : null,
    address.apartment ? `ap. ${address.apartment}` : null,
    address.doorCode ? `interfon ${address.doorCode}` : null,
  ];
  return parts.filter((part): part is string => part !== null && part.length > 0).join(" · ");
}
